import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import { useMyShows } from "@src/hooks/useMyShows";
import { useArchivedPlaylistCounts } from "@src/hooks/useArchivedPlaylistCounts";
import { useSubmittedPlaylistCounts } from "@src/hooks/useSubmittedPlaylistCounts";
import {
  useApiMasterScheduleEventsQuery,
  useCreateScheduleEventMutation,
  useSaveScheduleBoardMutation,
  useScheduleBoardQuery,
  useUpdateScheduleEventMutation,
} from "@src/queries/scheduleQueries";
import { fetchSchedulesByShowAround } from "@src/apis/scheduleApis";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { useRole, useAuthCurator } from "@src/hooks/useAuth";
import { useLocalStorageState } from "@src/hooks/useLocalStorageState";
import { FEATURES } from "@src/constants";
import UIModal from "@src/components/UIModal/UIModal";
import { IShowRecord } from "@src/types/show";
import ShowBox from "./ShowBox";
import DroppableCell from "./DroppableCell";
import ComparisonCell from "./ComparisonCell";
import MasterScheduleGrid from "./MasterScheduleGrid";
import { useTheme } from "styled-components";
import {
  ApplyRequest,
  DAYS,
  DragData,
  MasterGridItem,
  NoteShow,
  PlacedItem,
  ShowState,
  WEEKS_PER_BLOCK,
  WEEK_COUNT,
  blockStartHourLabel,
  blockTimeRange,
  cellId,
  deserializeBoard,
  formatNextAirDate,
  normalizePlacedItem,
  placedItemShowId,
  placedItemTitle,
  placementsFromMaster,
  resolveShowState,
  serializeBoard,
} from "./types";
import {
  BoardHeader,
  BoardLayout,
  BoardTitle,
  ConfirmApplyButton,
  ConfirmCancelButton,
  ConfirmError,
  ConfirmHighlight,
  ConfirmText,
  CountBadge,
  DragOverlayBox,
  GridCornerCell,
  GridHeaderCell,
  GridPanel,
  GridTable,
  HeaderActions,
  HeaderActionButton,
  HourLabelCell,
  NoteAddButton,
  NoteDeleteButton,
  NoteInput,
  NoteInputRow,
  NoteRow,
  NotesSection,
  PullMergeButton,
  SaveStatus,
  FilterButton,
  FilterControl,
  SearchInput,
  Sidebar,
  SidebarCount,
  SidebarEmpty,
  SidebarHeader,
  SidebarList,
  SidebarTitle,
  ShowsSection,
  StateDot,
  WhiteBlocksToggle,
  StateMenu,
  StateMenuBackdrop,
  StateMenuItem,
  StatusText,
  ViewToggle,
  ViewToggleButton,
  WeekCornerCell,
  WeekLabelCell,
} from "./ScheduleBoard.styles";

// Stable empty reference so cells with no master events don't get a fresh array
// each render.
const EMPTY_MASTER: MasterGridItem[] = [];

const WHITE_BLOCKS_STORAGE_KEY = "scheduleBoard.whiteBlocks";
const FILTERS_STORAGE_KEY = "scheduleBoard.filters";
const PLACEMENTS_STORAGE_KEY = "scheduleBoard.placements";
const NOTE_SHOWS_STORAGE_KEY = "scheduleBoard.noteShows";
const SHOW_STATES_STORAGE_KEY = "scheduleBoard.showStates";

// Multi-select toggles for the Shows panel. Combined with AND (a show must
// satisfy every enabled filter). Filters within a group are opposites, so
// enabling one clears the other in its group (a show can't be both on and off
// the board, nor have both zero and non-zero archived playlists).
type ShowFilter =
  | "active"
  | "onBoard"
  | "notOnBoard"
  | "hasArchive"
  | "noArchive"
  | "hasSubmitted"
  | "noSubmitted";

const EXCLUSIVE_GROUPS: ShowFilter[][] = [
  ["onBoard", "notOnBoard"],
  ["hasArchive", "noArchive"],
  ["hasSubmitted", "noSubmitted"],
];

const FILTER_OPTIONS: Array<{
  key: ShowFilter;
  label: string;
  title: string;
}> = [
  { key: "active", label: "Active", title: "Only active shows" },
  {
    key: "onBoard",
    label: "On board",
    title: "Only shows placed on the board",
  },
  {
    key: "notOnBoard",
    label: "Not on board",
    title: "Only shows not placed on the board",
  },
  {
    key: "hasArchive",
    label: "Has archive",
    title: "Only shows with one or more archived playlists",
  },
  {
    key: "noArchive",
    label: "No archive",
    title: "Only shows with zero archived playlists",
  },
  {
    key: "hasSubmitted",
    label: "Has submitted",
    title: "Only shows with one or more submitted playlists",
  },
  {
    key: "noSubmitted",
    label: "No submitted",
    title: "Only shows with zero submitted playlists",
  },
];

// May contain legacy bare-IShowRecord values from before note-shows existed;
// normalised on read.
type Placements = Record<string, unknown>;

const sortShows = (shows: IShowRecord[]): IShowRecord[] =>
  [...shows].sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
  );

const newNoteId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
};

const ScheduleBoard = (): React.JSX.Element => {
  const role = useRole();
  const curator = useAuthCurator();
  const [viewMode, setViewMode] = useState<"board" | "master" | "comparison">(
    "board"
  );
  const [whiteBlocks, setWhiteBlocks] = useLocalStorageState<boolean>(
    WHITE_BLOCKS_STORAGE_KEY,
    false
  );
  const [search, setSearch] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [filters, setFilters] = useLocalStorageState<ShowFilter[]>(
    FILTERS_STORAGE_KEY,
    []
  );
  const [placements, setPlacements] = useLocalStorageState<Placements>(
    PLACEMENTS_STORAGE_KEY,
    {}
  );
  const [noteShows, setNoteShows] = useLocalStorageState<NoteShow[]>(
    NOTE_SHOWS_STORAGE_KEY,
    []
  );
  // Manual state overrides, keyed by show documentId or `note:<id>` for notes
  // (absent => auto state).
  const [showStates, setShowStates] = useLocalStorageState<
    Record<string, ShowState>
  >(SHOW_STATES_STORAGE_KEY, {});
  const [stateMenu, setStateMenu] = useState<{
    key: string;
    current: ShowState;
    x: number;
    y: number;
  } | null>(null);
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
  // Pending comparison-view "Apply" confirmation (null when the dialog is
  // closed); `applying` guards against double-submits and `applyError` surfaces
  // a failed schedule write inside the dialog.
  const [applyTarget, setApplyTarget] = useState<ApplyRequest | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { mutateAsync: updateScheduleEvent } = useUpdateScheduleEventMutation();
  const { mutateAsync: createScheduleEvent } = useCreateScheduleEventMutation();
  const { mutateAsync: saveScheduleBoard } = useSaveScheduleBoardMutation();

  // Shared, server-side board persistence (admin-only, flagged). The board still
  // mirrors to localStorage; the server copy is the shared source of truth that
  // Save writes and the page hydrates from on load.
  const persistEnabled =
    FEATURES.SCHEDULE_BOARD_PERSIST_ENABLED && role === "admin";
  const { data: savedBoardResponse } = useScheduleBoardQuery({
    enabled: persistEnabled,
  });
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [pullOpen, setPullOpen] = useState(false);

  const { myShows, loadingMyShows, myShowsFetchingError } = useMyShows({
    curatorId: curator?.id ?? 0,
    roleType: role,
    searchText: "",
  });
  const { countsByShow, loadingCounts } = useArchivedPlaylistCounts();
  const { submittedByShow } = useSubmittedPlaylistCounts();

  // The master-schedule endpoint ignores the date range and returns every
  // show's next airing, so a single stable call feeds the whole board.
  const masterRangeAnchor = useMemo(() => new Date(), []);
  const { data: masterEventsResponse, isLoading: masterEventsLoading } =
    useApiMasterScheduleEventsQuery(masterRangeAnchor, masterRangeAnchor);
  // show documentId -> next scheduled air date (ISO), drawn from the master
  // schedule and surfaced on stopping shows.
  const nextAirByShow = useMemo(() => {
    const out: Record<string, string> = {};
    (masterEventsResponse?.data?.data ?? []).forEach((event) => {
      if (event.document_id && event.next_schedule) {
        out[event.document_id] = event.next_schedule;
      }
    });
    return out;
  }, [masterEventsResponse]);

  // Auto-place each master-schedule show onto the same grid the manual board
  // uses, from its real airing(s) (next airing + every parsed upcoming date).
  // The board's vertical axis is a day of 12 two-hour spots, each split into a
  // 4-week rotation, so a row (weekIndex 0..47) is:
  //   weekIndex = blockIndex * 4 + (isoWeek % 4)
  //     blockIndex = floor(localHour / 2)  -> 0..11 (top = 00:00–02:00)
  //     isoWeek % 4                        -> 0..3  (W1..W4 rotation)
  // Colour follows the same New/Regular rule as the board (green until a show
  // has an archive).
  const masterCells = useMemo(() => {
    const out: Record<string, MasterGridItem[]> = {};
    const events = masterEventsResponse?.data?.data ?? [];
    events.forEach((event) => {
      if (!event.document_id) return;
      const dates = new Set<string>();
      if (event.next_schedule) dates.add(event.next_schedule);
      if (event.upcoming_schedules) {
        event.upcoming_schedules
          .split("|")
          .forEach((iso) => iso && dates.add(iso));
      }
      dates.forEach((iso) => {
        const when = moment(iso);
        if (!when.isValid()) return;
        const blockIndex = Math.floor(when.hour() / 2); // 0..11 (2-hour spot)
        const weekInBlock = when.isoWeek() % WEEKS_PER_BLOCK; // 0..3 (W1..W4)
        const weekIndex = blockIndex * WEEKS_PER_BLOCK + weekInBlock;
        if (weekIndex < 0 || weekIndex >= WEEK_COUNT) return;
        const dayIndex = when.isoWeekday() - 1; // isoWeekday: Mon=1 => 0-based
        const id = cellId(dayIndex, weekIndex);
        const list = out[id] ?? (out[id] = []);
        if (list.some((item) => item.key === event.document_id)) return;
        const count = countsByShow[event.document_id] ?? 0;
        list.push({
          key: event.document_id,
          title: event.title,
          subtitle: event.curator || undefined,
          state: loadingCounts ? "regular" : resolveShowState(undefined, count),
          count,
          slotTime: iso,
          frequency: event.frequency,
        });
      });
    });
    return out;
  }, [masterEventsResponse, countsByShow, loadingCounts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const { setNodeRef: setSidebarRef } = useDroppable({
    id: "sidebar",
    data: { type: "sidebar" },
  });

  // Normalise stored placements (which may still hold legacy bare-show values)
  // into the discriminated PlacedItem shape used throughout the board.
  const cells = useMemo(() => {
    const out: Record<string, PlacedItem> = {};
    Object.entries(placements).forEach(([id, value]) => {
      const item = normalizePlacedItem(value);
      if (item) out[id] = item;
    });
    return out;
  }, [placements]);

  // Live shows keyed by documentId, used to rehydrate persisted show refs and to
  // resolve master-schedule shows when pulling them onto the board.
  const showsById = useMemo(() => {
    const out: Record<string, IShowRecord> = {};
    myShows.forEach((show) => {
      out[show.documentId] = show;
    });
    return out;
  }, [myShows]);

  // Show documentIds currently placed on the grid — drives the "Not on board"
  // filter. A show stays in the sidebar even once placed, so a show can be both
  // available and on the board.
  const placedShowIds = useMemo(() => {
    const ids = new Set<string>();
    Object.values(cells).forEach((item) => {
      const id = placedItemShowId(item);
      if (id) ids.add(id);
    });
    return ids;
  }, [cells]);

  // Shows stay in the sidebar even once placed, so the same entry can be
  // dropped into multiple slots. Search plus the active filters (AND) narrow
  // the list; results are kept alphabetical.
  const availableShows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = myShows.filter((show) => {
      if (term !== "" && !show.title.toLowerCase().includes(term)) return false;
      if (filters.includes("active") && !show.active) return false;
      const onBoard = placedShowIds.has(show.documentId);
      if (filters.includes("onBoard") && !onBoard) return false;
      if (filters.includes("notOnBoard") && onBoard) return false;
      const archived = countsByShow[show.documentId] ?? 0;
      if (filters.includes("hasArchive") && archived < 1) return false;
      if (filters.includes("noArchive") && archived > 0) return false;
      const submitted = submittedByShow[show.documentId] ?? 0;
      if (filters.includes("hasSubmitted") && submitted < 1) return false;
      if (filters.includes("noSubmitted") && submitted > 0) return false;
      return true;
    });
    return sortShows(filtered);
  }, [myShows, search, filters, placedShowIds, countsByShow, submittedByShow]);

  const toggleFilter = (key: ShowFilter): void => {
    setFilters((prev) => {
      if (prev.includes(key)) return prev.filter((f) => f !== key);
      // Enabling a filter clears its opposite within the same exclusive group.
      const group = EXCLUSIVE_GROUPS.find((g) => g.includes(key));
      if (group) return [...prev.filter((f) => !group.includes(f)), key];
      return [...prev, key];
    });
  };

  // While archived counts are still loading, don't auto-assign "new" (that made
  // every show flash green before flipping to white); fall back to "regular"
  // unless the user set an explicit override.
  const stateForShow = (documentId: string): ShowState => {
    const override = showStates[documentId];
    if (override) return override;
    if (loadingCounts) return "regular";
    return resolveShowState(undefined, countsByShow[documentId] ?? 0);
  };

  const noteStateKey = (noteId: string): string => `note:${noteId}`;

  // Notes have no archived playlists, so their default is "regular" (white);
  // the asterisk menu can still override them.
  const stateForNote = (noteId: string): ShowState =>
    showStates[noteStateKey(noteId)] ?? "regular";

  const stateForItem = (item: PlacedItem): ShowState =>
    item.kind === "show"
      ? stateForShow(item.show.documentId)
      : stateForNote(item.note.id);

  const stateKeyForItem = (item: PlacedItem): string =>
    item.kind === "show" ? item.show.documentId : noteStateKey(item.note.id);

  // Next-air label ("MM.DD" / "—") shown only when a real show is stopping;
  // notes and non-stopping shows get nothing.
  const nextAirLabel = (
    documentId: string,
    state: ShowState
  ): string | undefined =>
    state === "stopping"
      ? formatNextAirDate(nextAirByShow[documentId])
      : undefined;

  const handleDragStart = (event: DragStartEvent): void => {
    const data = event.active.data.current as DragData | undefined;
    if (data) setActiveDrag(data);
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current as DragData | undefined;
    const overData = over.data.current as
      | { type: "cell"; cellId: string }
      | { type: "sidebar" }
      | undefined;
    if (!activeData || !overData) return;

    // Drag a placed item back to the sidebar => remove it from the grid.
    if (overData.type === "sidebar") {
      if (activeData.type === "placed") {
        setPlacements((prev) => {
          const next = { ...prev };
          delete next[activeData.fromCellId];
          return next;
        });
      }
      return;
    }

    const targetCellId = overData.cellId;
    const targetOccupied = Boolean(cells[targetCellId]);

    if (activeData.type === "inventory-show") {
      if (targetOccupied) return; // one entry per cell
      const item: PlacedItem = { kind: "show", show: activeData.show };
      setPlacements((prev) => ({ ...prev, [targetCellId]: item }));
      return;
    }

    if (activeData.type === "inventory-note") {
      if (targetOccupied) return; // one entry per cell
      const item: PlacedItem = { kind: "note", note: activeData.note };
      setPlacements((prev) => ({ ...prev, [targetCellId]: item }));
      return;
    }

    // Moving an already-placed item to another cell.
    if (activeData.fromCellId === targetCellId) return;
    if (targetOccupied) return; // one entry per cell
    setPlacements((prev) => {
      const next = { ...prev };
      delete next[activeData.fromCellId];
      next[targetCellId] = activeData.item;
      return next;
    });
  };

  const handleRemove = (id: string): void => {
    setPlacements((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleAddNote = (event: React.FormEvent): void => {
    event.preventDefault();
    const title = noteDraft.trim();
    if (!title) return;
    setNoteShows((prev) => [...prev, { id: newNoteId(), title }]);
    setNoteDraft("");
  };

  const handleRemoveNote = (id: string): void => {
    setNoteShows((prev) => prev.filter((note) => note.id !== id));
  };

  const openStateMenu = (
    key: string,
    current: ShowState,
    x: number,
    y: number
  ): void => setStateMenu({ key, current, x, y });

  const applyState = (state: ShowState): void => {
    if (!stateMenu) return;
    const { key } = stateMenu;
    setShowStates((prev) => ({ ...prev, [key]: state }));
    setStateMenu(null);
  };

  const resetStateToAuto = (): void => {
    if (!stateMenu) return;
    const { key } = stateMenu;
    setShowStates((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setStateMenu(null);
  };

  // Hydrate the board from the shared server copy once, after shows have loaded
  // (so show refs resolve). Skipped when the server has no saved board yet, which
  // keeps whatever is already in localStorage.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!persistEnabled || hydratedRef.current || loadingMyShows) return;
    const state = savedBoardResponse?.data?.data?.state;
    if (!state) return;
    const {
      placements: nextPlacements,
      noteShows: nextNotes,
      showStates: nextStates,
    } = deserializeBoard(state, showsById);
    setPlacements(nextPlacements as Placements);
    setNoteShows(nextNotes);
    setShowStates(nextStates);
    hydratedRef.current = true;
  }, [
    persistEnabled,
    loadingMyShows,
    savedBoardResponse,
    showsById,
    setPlacements,
    setNoteShows,
    setShowStates,
  ]);

  // A "Saved" badge is only truthful until the next edit.
  useEffect(() => {
    setSaveStatus((prev) => (prev === "saved" ? "idle" : prev));
  }, [placements, noteShows, showStates]);

  const handleSaveBoard = async (): Promise<void> => {
    setSaveStatus("saving");
    try {
      await saveScheduleBoard({
        state: serializeBoard(cells, noteShows, showStates),
      });
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
    }
  };

  // Pull the auto-placed master schedule onto the editable board. "replace" swaps
  // the whole board; "merge" only fills cells the board hasn't already claimed.
  const applyPull = (mode: "replace" | "merge"): void => {
    const fromMaster = placementsFromMaster(masterCells, showsById);
    setPlacements((prev) =>
      mode === "replace"
        ? (fromMaster as Placements)
        : { ...fromMaster, ...prev }
    );
    setPullOpen(false);
  };

  const activeOriginCellId =
    activeDrag?.type === "placed" ? activeDrag.fromCellId : null;
  const isComparison = viewMode === "comparison";
  // Applying a board show into a live master slot is a write, gated to admins
  // (the board route is already admin-only) behind the rollout flag.
  const canApply = FEATURES.SCHEDULE_BOARD_APPLY_ENABLED && role === "admin";

  const requestApply = (request: ApplyRequest): void => {
    setApplyError(null);
    setApplyTarget(request);
  };

  const closeApply = (): void => {
    if (applying) return;
    setApplyTarget(null);
    setApplyError(null);
  };

  const confirmApply = async (): Promise<void> => {
    if (!applyTarget) return;
    const boardShowId = placedItemShowId(applyTarget.boardItem);
    if (!boardShowId) {
      setApplyError("Only real shows can be applied to the schedule.");
      return;
    }
    const startTime = moment(applyTarget.slotTime);
    const endTime = startTime.clone().add(2, "hours");
    setApplying(true);
    setApplyError(null);
    try {
      // Find the concrete schedule event backing the master slot so we replace
      // that airing's show rather than creating a duplicate. Pick the event
      // whose startTime is closest to the slot (the master endpoint's time can
      // differ slightly from the stored event's).
      const existing = await fetchSchedulesByShowAround(
        applyTarget.masterShowId,
        applyTarget.slotTime
      );
      const candidates = existing.data?.data ?? [];
      const closest = candidates.reduce<{
        documentId: string;
        diff: number;
      } | null>((best, event) => {
        const diff = Math.abs(
          moment(event.startTime).diff(startTime, "minutes")
        );
        if (!best || diff < best.diff)
          return { documentId: event.documentId, diff };
        return best;
      }, null);

      if (closest) {
        await updateScheduleEvent({
          data: {
            scheduleId: closest.documentId,
            showId: boardShowId,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
          },
        });
      } else {
        // No existing event resolved for the slot — create one so the board show
        // still lands on the schedule at the intended time.
        await createScheduleEvent({
          data: {
            showId: boardShowId,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
          },
        });
      }

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_MASTER_SCHEDULE],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_SCHEDULE],
      });
      setApplyTarget(null);
    } catch {
      setApplyError("Could not apply the show to the schedule. Please retry.");
    } finally {
      setApplying(false);
    }
  };

  const stateOptions: Array<{ key: ShowState; label: string; color: string }> =
    [
      { key: "new", label: "New", color: theme.colors.green },
      { key: "regular", label: "Regular", color: theme.colors.white },
      { key: "stopping", label: "Stopping", color: theme.colors.red },
    ];
  const menuOverride = stateMenu ? showStates[stateMenu.key] : undefined;
  const menuResolved = stateMenu?.current;

  const overlay = ((): {
    title: string;
    state?: ShowState;
    count?: number;
  } | null => {
    if (!activeDrag) return null;
    if (activeDrag.type === "inventory-show")
      return {
        title: activeDrag.show.title,
        state: stateForShow(activeDrag.show.documentId),
        count: countsByShow[activeDrag.show.documentId],
      };
    if (activeDrag.type === "inventory-note")
      return {
        title: activeDrag.note.title,
        state: stateForNote(activeDrag.note.id),
      };
    const showId = placedItemShowId(activeDrag.item);
    return {
      title: placedItemTitle(activeDrag.item),
      state: stateForItem(activeDrag.item),
      count: showId ? countsByShow[showId] : undefined,
    };
  })();

  const renderShowsBody = (): React.JSX.Element => {
    if (loadingMyShows) return <StatusText>Loading shows…</StatusText>;
    if (myShowsFetchingError)
      return <StatusText>Could not load shows.</StatusText>;
    if (availableShows.length === 0)
      return (
        <SidebarEmpty>
          {search.trim() || filters.length > 0
            ? "No shows match your filters."
            : "No shows yet."}
        </SidebarEmpty>
      );
    return (
      <>
        {availableShows.map((show) => (
          <ShowBox
            key={show.documentId}
            id={`inventory-show:${show.documentId}`}
            title={show.title}
            subtitle={show.curators?.[0]?.name}
            state={stateForShow(show.documentId)}
            count={countsByShow[show.documentId]}
            submittedCount={submittedByShow[show.documentId]}
            nextAirDate={nextAirLabel(
              show.documentId,
              stateForShow(show.documentId)
            )}
            data={{ type: "inventory-show", show }}
          />
        ))}
      </>
    );
  };

  return (
    <WrapperContainer noScroll>
      <PageTopSection slim>
        <BoardHeader>
          <ViewToggle role="tablist" aria-label="Schedule board view">
            <ViewToggleButton
              type="button"
              role="tab"
              aria-selected={viewMode === "board"}
              $active={viewMode === "board"}
              onClick={(): void => setViewMode("board")}
            >
              Board
            </ViewToggleButton>
            <ViewToggleButton
              type="button"
              role="tab"
              aria-selected={viewMode === "master"}
              $active={viewMode === "master"}
              onClick={(): void => setViewMode("master")}
            >
              Master Schedule
            </ViewToggleButton>
            <ViewToggleButton
              type="button"
              role="tab"
              aria-selected={viewMode === "comparison"}
              $active={viewMode === "comparison"}
              onClick={(): void => setViewMode("comparison")}
            >
              Comparison
            </ViewToggleButton>
          </ViewToggle>
          <BoardTitle>Schedule Board</BoardTitle>
          <HeaderActions>
            {persistEnabled && viewMode !== "master" ? (
              <>
                {saveStatus === "saved" ? (
                  <SaveStatus>Saved</SaveStatus>
                ) : saveStatus === "error" ? (
                  <SaveStatus $error>Save failed</SaveStatus>
                ) : null}
                <HeaderActionButton
                  type="button"
                  onClick={(): void => setPullOpen(true)}
                  disabled={masterEventsLoading}
                  title="Pull the master schedule onto the board"
                >
                  Pull master schedule
                </HeaderActionButton>
                <HeaderActionButton
                  type="button"
                  $primary
                  onClick={handleSaveBoard}
                  disabled={saveStatus === "saving"}
                >
                  {saveStatus === "saving" ? "Saving…" : "Save"}
                </HeaderActionButton>
              </>
            ) : null}
            <WhiteBlocksToggle
              type="button"
              aria-pressed={whiteBlocks}
              $active={whiteBlocks}
              onClick={(): void => setWhiteBlocks((prev) => !prev)}
            >
              White blocks
            </WhiteBlocksToggle>
          </HeaderActions>
        </BoardHeader>
      </PageTopSection>
      <ContentContainer>
        {viewMode === "master" ? (
          <MasterScheduleGrid
            cells={masterCells}
            loading={masterEventsLoading}
            whiteBlocks={whiteBlocks}
          />
        ) : (
          <>
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <BoardLayout>
                <Sidebar ref={setSidebarRef}>
                  <ShowsSection>
                    <SidebarHeader>
                      <SidebarTitle>Shows</SidebarTitle>
                      <SidebarCount>{availableShows.length}</SidebarCount>
                    </SidebarHeader>
                    <FilterControl role="group" aria-label="Filter shows">
                      {FILTER_OPTIONS.map((option) => {
                        const active = filters.includes(option.key);
                        return (
                          <FilterButton
                            key={option.key}
                            type="button"
                            title={option.title}
                            $active={active}
                            aria-pressed={active}
                            onClick={(): void => toggleFilter(option.key)}
                          >
                            {option.label}
                          </FilterButton>
                        );
                      })}
                    </FilterControl>
                    <SearchInput
                      placeholder="Search shows"
                      value={search}
                      onChange={(e): void => setSearch(e.target.value)}
                    />
                    <SidebarList>{renderShowsBody()}</SidebarList>
                  </ShowsSection>

                  <NotesSection>
                    <SidebarHeader>
                      <SidebarTitle>Note Shows</SidebarTitle>
                      <SidebarCount>{noteShows.length}</SidebarCount>
                    </SidebarHeader>
                    <NoteInputRow onSubmit={handleAddNote}>
                      <NoteInput
                        placeholder="Add a note-show"
                        value={noteDraft}
                        onChange={(e): void => setNoteDraft(e.target.value)}
                        aria-label="New note-show title"
                      />
                      <NoteAddButton type="submit" disabled={!noteDraft.trim()}>
                        Add
                      </NoteAddButton>
                    </NoteInputRow>
                    <SidebarList>
                      {noteShows.length === 0 ? (
                        <SidebarEmpty>No note-shows yet.</SidebarEmpty>
                      ) : (
                        noteShows.map((note) => (
                          <NoteRow key={note.id}>
                            <ShowBox
                              id={`inventory-note:${note.id}`}
                              title={note.title}
                              state={stateForNote(note.id)}
                              data={{ type: "inventory-note", note }}
                            />
                            <NoteDeleteButton
                              type="button"
                              aria-label={`Remove ${note.title}`}
                              onClick={(): void => handleRemoveNote(note.id)}
                            >
                              ×
                            </NoteDeleteButton>
                          </NoteRow>
                        ))
                      )}
                    </SidebarList>
                  </NotesSection>
                </Sidebar>

                <GridPanel>
                  <GridTable>
                    <GridCornerCell />
                    <WeekCornerCell />
                    {DAYS.map((day) => (
                      <GridHeaderCell key={day}>{day}</GridHeaderCell>
                    ))}
                    {Array.from({ length: WEEK_COUNT }, (_, weekIndex) => {
                      const monthStart = weekIndex % WEEKS_PER_BLOCK === 0;
                      return (
                        <React.Fragment key={weekIndex}>
                          {monthStart ? (
                            <HourLabelCell
                              $span={WEEKS_PER_BLOCK}
                              title={blockTimeRange(weekIndex)}
                            >
                              {blockStartHourLabel(weekIndex)}
                            </HourLabelCell>
                          ) : null}
                          <WeekLabelCell
                            $monthStart={monthStart}
                            title={`${blockTimeRange(weekIndex)} · W${
                              (weekIndex % WEEKS_PER_BLOCK) + 1
                            }`}
                          >
                            W{(weekIndex % WEEKS_PER_BLOCK) + 1}
                          </WeekLabelCell>
                          {DAYS.map((_, dayIndex) => {
                            const id = cellId(dayIndex, weekIndex);
                            const occupant = cells[id];
                            const showId = occupant
                              ? placedItemShowId(occupant)
                              : null;
                            const commonProps = {
                              cellId: id,
                              monthStart,
                              occupant,
                              occupantState: occupant
                                ? stateForItem(occupant)
                                : undefined,
                              occupantStateKey: occupant
                                ? stateKeyForItem(occupant)
                                : undefined,
                              occupantCount: showId
                                ? countsByShow[showId]
                                : undefined,
                              occupantNextAirDate:
                                occupant && showId
                                  ? nextAirLabel(showId, stateForItem(occupant))
                                  : undefined,
                              whiteBlocks,
                              activeOriginCellId,
                              onRemove: handleRemove,
                              onOpenStateMenu: openStateMenu,
                            };
                            return isComparison ? (
                              <ComparisonCell
                                key={id}
                                {...commonProps}
                                masterItems={masterCells[id] ?? EMPTY_MASTER}
                                canApply={canApply}
                                onApply={requestApply}
                              />
                            ) : (
                              <DroppableCell key={id} {...commonProps} />
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </GridTable>
                </GridPanel>
              </BoardLayout>

              <DragOverlay>
                {overlay ? (
                  <DragOverlayBox
                    $state={overlay.state}
                    $whiteBlocks={whiteBlocks}
                  >
                    {overlay.title}
                    {typeof overlay.count === "number" ? (
                      <CountBadge>({overlay.count})</CountBadge>
                    ) : null}
                  </DragOverlayBox>
                ) : null}
              </DragOverlay>
            </DndContext>

            {stateMenu ? (
              <>
                <StateMenuBackdrop onClick={(): void => setStateMenu(null)} />
                <StateMenu $x={stateMenu.x} $y={stateMenu.y}>
                  {stateOptions.map((option) => (
                    <StateMenuItem
                      key={option.key}
                      type="button"
                      $active={menuResolved === option.key}
                      onClick={(): void => applyState(option.key)}
                    >
                      <StateDot $color={option.color} />
                      {option.label}
                    </StateMenuItem>
                  ))}
                  <StateMenuItem
                    type="button"
                    $active={menuOverride === undefined}
                    onClick={resetStateToAuto}
                  >
                    Auto
                  </StateMenuItem>
                </StateMenu>
              </>
            ) : null}

            <UIModal
              isOpen={Boolean(applyTarget)}
              onClose={closeApply}
              title="Apply to schedule"
              hideCloseButton={applying}
              actions={
                <>
                  <ConfirmCancelButton
                    type="button"
                    onClick={closeApply}
                    disabled={applying}
                  >
                    Cancel
                  </ConfirmCancelButton>
                  <ConfirmApplyButton
                    type="button"
                    onClick={confirmApply}
                    disabled={applying}
                  >
                    {applying ? "Applying…" : "Apply"}
                  </ConfirmApplyButton>
                </>
              }
            >
              {applyTarget ? (
                <>
                  <ConfirmText>
                    Schedule{" "}
                    <ConfirmHighlight>
                      {placedItemTitle(applyTarget.boardItem)}
                    </ConfirmHighlight>{" "}
                    into the master slot on{" "}
                    <ConfirmHighlight>
                      {moment(applyTarget.slotTime).format(
                        "dddd, MMM D YYYY · HH:mm"
                      )}
                    </ConfirmHighlight>
                    , replacing{" "}
                    <ConfirmHighlight>
                      {applyTarget.masterTitle}
                    </ConfirmHighlight>
                    ?
                  </ConfirmText>
                  {applyError ? (
                    <ConfirmError>{applyError}</ConfirmError>
                  ) : null}
                </>
              ) : null}
            </UIModal>

            <UIModal
              isOpen={pullOpen}
              onClose={(): void => setPullOpen(false)}
              title="Pull master schedule"
              actions={
                <>
                  <ConfirmCancelButton
                    type="button"
                    onClick={(): void => setPullOpen(false)}
                  >
                    Cancel
                  </ConfirmCancelButton>
                  <PullMergeButton
                    type="button"
                    onClick={(): void => applyPull("merge")}
                  >
                    Merge
                  </PullMergeButton>
                  <ConfirmApplyButton
                    type="button"
                    onClick={(): void => applyPull("replace")}
                  >
                    Replace board
                  </ConfirmApplyButton>
                </>
              }
            >
              <ConfirmText>
                Pull the current master schedule onto the board.{" "}
                <ConfirmHighlight>Merge</ConfirmHighlight> keeps your current
                placements and only fills empty cells.{" "}
                <ConfirmHighlight>Replace board</ConfirmHighlight> clears the
                board and loads the master schedule fresh. Either way, nothing
                is written to the live schedule until you Save.
              </ConfirmText>
            </UIModal>
          </>
        )}
      </ContentContainer>
    </WrapperContainer>
  );
};

export default ScheduleBoard;
